import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useTranslation } from 'react-i18next';
import { Vehicle } from '../../types/vehicle';
import { fleetService } from '../../services/fleetService';
import { vehicleService } from '../../services/vehicleService';
import FleetAvailabilityTimeline from './FleetAvailabilityTimeline';
import FleetAvailabilityWeekView from './FleetAvailabilityWeekView';
import { addDays, subDays, format, startOfWeek } from 'date-fns';
import { fr } from 'date-fns/locale';

interface AdminCalendarViewProps {
  organizationId: string;
}

type ViewMode = 'day' | 'week';

interface FleetWithCount {
  id: string;
  name: string;
  description?: string;
  vehicleCount: number;
  [key: string]: any;
}

const AdminCalendarView: React.FC<AdminCalendarViewProps> = ({ organizationId }) => {
  const { t } = useTranslation();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<ViewMode>('day');
  const [loading, setLoading] = useState(true);
  const [fleets, setFleets] = useState<FleetWithCount[]>([]);
  const [selectedFleetId, setSelectedFleetId] = useState<string>('');
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);

  useEffect(() => {
    loadFleets();
  }, [organizationId]);

  useEffect(() => {
    if (selectedFleetId) {
      loadVehicles();
    }
  }, [selectedFleetId]);

  const loadFleets = async () => {
    setLoading(true);
    try {
      const fleetsData = await fleetService.getFleets(organizationId);

      // Charger le nombre de véhicules pour chaque flotte
      const fleetsWithCounts = await Promise.all(
        fleetsData.map(async (fleet) => {
          try {
            const vehiclesData = await vehicleService.getVehiclesByFleet(fleet.id);
            return {
              ...fleet,
              vehicleCount: vehiclesData.length
            };
          } catch (error) {
            console.error(`Error loading vehicles for fleet ${fleet.id}:`, error);
            return {
              ...fleet,
              vehicleCount: 0
            };
          }
        })
      );

      setFleets(fleetsWithCounts);

      // Sélectionner la première flotte par défaut
      if (fleetsWithCounts.length > 0) {
        setSelectedFleetId(fleetsWithCounts[0].id);
      }
    } catch (error) {
      console.error('Error loading fleets:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadVehicles = async () => {
    try {
      const vehiclesData = await vehicleService.getVehiclesByFleet(selectedFleetId);
      setVehicles(vehiclesData);
    } catch (error) {
      console.error('Error loading vehicles:', error);
    }
  };

  const handlePrevious = () => {
    if (viewMode === 'day') {
      setCurrentDate(subDays(currentDate, 1));
    } else {
      setCurrentDate(subDays(currentDate, 7));
    }
  };

  const handleNext = () => {
    if (viewMode === 'day') {
      setCurrentDate(addDays(currentDate, 1));
    } else {
      setCurrentDate(addDays(currentDate, 7));
    }
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const formatDateDisplay = () => {
    if (viewMode === 'day') {
      return format(currentDate, 'EEEE dd MMMM yyyy', { locale: fr });
    } else {
      const weekStart = startOfWeek(currentDate, { locale: fr, weekStartsOn: 1 });
      const weekEnd = addDays(weekStart, 6);
      return `${format(weekStart, 'dd MMM', { locale: fr })} - ${format(weekEnd, 'dd MMM yyyy', { locale: fr })}`;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (fleets.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">
          {t('reservation:calendar.noFleets', 'Aucune flotte disponible. Créez une flotte pour gérer les réservations.')}
        </Typography>
      </Paper>
    );
  }

  const selectedFleet = fleets.find(f => f.id === selectedFleetId);

  return (
    <Box>
      {/* Sélecteur de flotte */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <FormControl fullWidth>
          <InputLabel>{t('reservation:calendar.selectFleet', 'Sélectionner une flotte')}</InputLabel>
          <Select
            value={selectedFleetId}
            onChange={(e) => setSelectedFleetId(e.target.value)}
            label={t('reservation:calendar.selectFleet', 'Sélectionner une flotte')}
          >
            {fleets.map((fleet) => (
              <MenuItem key={fleet.id} value={fleet.id}>
                {fleet.name} ({fleet.vehicleCount || 0} {t('common.vehicles', 'véhicules')})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>

      {/* Contrôles de navigation */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <IconButton onClick={handlePrevious} size="small">
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" sx={{ minWidth: 280, textAlign: 'center' }}>
              {formatDateDisplay()}
            </Typography>
            <IconButton onClick={handleNext} size="small">
              <ArrowForwardIcon />
            </IconButton>
            <Button
              variant="outlined"
              size="small"
              onClick={handleToday}
              sx={{ ml: 2, textTransform: 'none' }}
            >
              {t('reservation:calendar.today', "Aujourd'hui")}
            </Button>
          </Box>

          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, newMode) => newMode && setViewMode(newMode)}
            size="small"
          >
            <ToggleButton value="day">
              {t('reservation:calendar.day', 'Jour')}
            </ToggleButton>
            <ToggleButton value="week">
              {t('reservation:calendar.week', 'Semaine')}
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Paper>

      {/* Calendrier */}
      {selectedFleet && vehicles.length > 0 ? (
        viewMode === 'day' ? (
          <FleetAvailabilityTimeline
            fleetId={selectedFleetId}
            vehicles={vehicles}
            date={currentDate}
            startHour={8}
            endHour={20}
          />
        ) : (
          <FleetAvailabilityWeekView
            fleetId={selectedFleetId}
            vehicles={vehicles}
            weekStartDate={startOfWeek(currentDate, { locale: fr, weekStartsOn: 1 })}
            startHour={8}
            endHour={20}
          />
        )
      ) : (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {t('reservation:calendar.noVehicles', 'Aucun véhicule dans cette flotte')}
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default AdminCalendarView;
